import { Injectable, Injector, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { sortBy } from 'osn-shared';
import { Observable, Subject, switchMap, of } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { RootAppState } from '../../../../app.reducer';
import { selectCxbCountries, selectCxbUserSegments } from '../../../../app.selectors';
import { Country } from '../../../../domain/country';
import { RedeemPageVersion } from '../../../../domain/page-version';
import { Platform } from '../../../../domain/platform';
import { Tenant } from '../../../../domain/tenant';
import { UserSegment } from '../../../../domain/user-segment';
import { RouterState } from '../../../../router.selectors';
import { ConfirmDialogService } from '../../../../shared/components/confirm-dialog/confirm-dialog.service';
import { mapCountriesToViewModel, mapUserSegmentsToViewModel } from '../../../../shared/functions/map-page-to-edit-page-view-model.helper';
import { mapDomainPageVersionToVm } from '../../../mapping/map-domain-page-version-to-vm';
import {
  createCxbPageDraft,
  deleteCxbPageDraft,
  loadCxbPageEditScreen,
  publishCxbPageDraft,
  unpublishCxbPage,
} from '../../../page-edit-screen.actions';
import { AppState } from '../../../page-edit-screen.reducer';
import { selectCxbPage } from '../../../page-edit-screen.selectors';
import { PageType } from '../../../page-type';
import { PageEditDialogService } from '../page-edit-dialog.service';
import { PageEditScreenViewModel, PageEditUserSegment } from '../page-edit-screen.view-model';

@Injectable({
  providedIn: 'root',
})
export class PageEditScreenService implements OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private readonly dialogService: PageEditDialogService;
  private selectedUserSegments: UserSegment[];
  private selectedCountries: Country[];
  private selectedPlatforms: Platform[];
  public readonly pageType: PageType;

  private readonly domainPage$ = this.store.select(selectCxbPage).pipe(filter((page) => !!page));

  public vm$: Observable<PageEditScreenViewModel> = this.domainPage$.pipe(
    tap((refactPage) => {
      this.selectedCountries = (refactPage as RedeemPageVersion).countries || [];
      this.selectedUserSegments = (refactPage as RedeemPageVersion).userSegments || [];
      this.selectedPlatforms = (refactPage as RedeemPageVersion).platforms ?? [];
    }),
    map((refactPage) => mapDomainPageVersionToVm(refactPage)),
    map((vm) => ({ ...vm, trays: sortBy(vm.trays, (tray) => tray.order) }))
  );

  public userSegments$: Observable<PageEditUserSegment[]> = this.rootStore
    .select(selectCxbUserSegments)
    .pipe(map((userSegments: UserSegment[]) => mapUserSegmentsToViewModel(userSegments)));

  public countries$ = this.rootStore.select(selectCxbCountries).pipe(map((countries: Country[]) => mapCountriesToViewModel(countries)));

  constructor(
    private readonly store: Store<AppState>,
    private readonly rootStore: Store<RootAppState & RouterState>,
    private readonly route: ActivatedRoute,
    injector: Injector,
    private readonly confirmDialogService: ConfirmDialogService
  ) {
    this.dialogService = injector.get(route.snapshot.data['dialogServiceToken']);

    const routeData = route.snapshot.data?.pageType;

    if (!(routeData instanceof PageType)) {
      throw Error('Page type is required for page editing');
    }

    this.pageType = routeData;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public loadPage(pageVersionId: string, userSegments: string[], countryCodes: string[], selectedTenant: Tenant): void {
    this.store.dispatch(
      loadCxbPageEditScreen.sourceAction({ pageVersionId, userSegments, countryCodes, pageType: this.pageType, selectedTenant })
    );
  }

  public openPublishPageModal(pageId: string, title: string) {
    this.openConfirmationPopupWithResult$(title)
      .pipe(
        switchMap((result: boolean) => {
          if (result) {
            this.store.dispatch(publishCxbPageDraft.sourceAction({ pageId, pageType: this.pageType }));
          }
          return of(null);
        })
      )
      .subscribe();
  }

  public deleteDraft(pageId: string): void {
    this.store.dispatch(deleteCxbPageDraft.sourceAction({ pageId, pageType: this.pageType }));
  }

  public unpublishPage(pageId: string): void {
    this.store.dispatch(unpublishCxbPage.sourceAction({ pageId, pageType: this.pageType }));
  }

  public createDraft(pageId: string): void {
    this.store.dispatch(createCxbPageDraft.sourceAction({ pageId, pageType: this.pageType }));
  }

  public openEditMetaDialog(viewModel: PageEditScreenViewModel, readonly: boolean): void {
    this.dialogService.openEditMetaDialog(
      viewModel,
      readonly,
      this.pageType,
      this.selectedCountries,
      this.selectedUserSegments,
      this.selectedPlatforms
    );
  }

  private openConfirmationPopupWithResult$(title: string): Observable<boolean> {
    return this.confirmDialogService
      .openDialog({
        title: `Publish changes to ${title} screen?\nAre you sure you want to publish changes of this page to the live applications?`,
        cancelBtnLabel: 'Cancel',
        confirmBtnLabel: 'Publish to live',
      })
      .afterClosed();
  }
}
